/**
 * Seções Regulated da Tela 2 (T2-3): Bubbles · Resumos IA (honest-defer).
 *
 * Bubbles vêm do contrato REAL (gated server-side por `confidentiality-bubbles-full`;
 * chegam `[]` sem a cap — então a seção só aparece quando há dado). Resumos IA
 * é honest-defer real (`summaries: []` sempre — Item 338): empty state honesto,
 * SEM texto fabricado. Audit per-person não tem endpoint consolidado — omitido
 * (sem inventar "N visualizações nas últimas 24h").
 */

import { AlertTriangle } from 'lucide-react';

import { PersonSection } from './PersonSection';

import type { PersonBubble } from './types';

const VISIBILITY_LABEL: Record<string, string> = {
  'only-creator': 'só criador',
  'creator-and-specific-roles': 'criador + papéis',
  'creator-and-list': 'criador + lista',
};

// ─── 7. Resumos & padrões (IA) — honest-defer (Item 338) ────────────────

export function SummariesSection(): JSX.Element {
  return (
    <PersonSection id="summaries" label="Resumos & padrões (IA)">
      <div className="person-empty">
        Resumos e padrões gerados por IA para a pessoa ainda não estão disponíveis nesta versão.
        Quando a fonte de IA for habilitada, o perfil consolidado aparecerá aqui.
      </div>
    </PersonSection>
  );
}

// ─── 8. Bubbles · Adversarialidade (Regulated) ──────────────────────────

export function BubblesSection({ bubbles }: { bubbles: PersonBubble[] }): JSX.Element | null {
  if (bubbles.length === 0) return null;
  return (
    <PersonSection id="bubbles" label="Bubbles · Adversarialidade" count={bubbles.length}>
      <div className="muted mono" style={{ fontSize: 10, marginBottom: 6 }}>
        Conflitos, mediações e bloqueios cruzados — só visível para quem tem a capability sensível.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {bubbles.map((b) => (
          <div key={b.id} className="bubble-card">
            <span className="bubble-card__glyph">
              <AlertTriangle size={14} />
            </span>
            <div className="bubble-card__body">
              <span className="bubble-card__title">{b.name}</span>
              <span className="bubble-card__meta">
                bond #{b.bondId.slice(0, 8)} · {b.topic}
                {b.reason !== null && b.reason !== '' && <> · {b.reason}</>}
              </span>
            </div>
            <span className="bubble-card__state">
              {VISIBILITY_LABEL[b.visibilityMode] ?? b.visibilityMode}
            </span>
          </div>
        ))}
      </div>
    </PersonSection>
  );
}

// ─── Alerta de mediação adversarial (topo) — derivado de bond adversarial ──

export function AdversarialMediationAlert(): JSX.Element {
  return (
    <div className="regulated-alert">
      <AlertTriangle size={13} />
      <div style={{ flex: 1 }}>
        <strong>Pessoa em bond com mediação adversarial.</strong> Mensagens cruzadas entre partes
        podem estar sob restrição de visibilidade. Verifique as bubbles antes de compartilhar
        conteúdo entre os lados.
      </div>
    </div>
  );
}
